import React from "react";
import {
  AbsoluteFill,
  Img,
  Audio,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  Easing,
  random,
} from "remotion";

/**
 * TeaserMontage — Yamunotri v2 (15s @ 24fps, suspense music + visible paper tears)
 *
 * Architecture:
 *  - Each slot's Sequence runs PER_SLOT + TRANSITION_FRAMES so consecutive
 *    slots OVERLAP during the tear. The newer Sequence stacks on top with a
 *    growing-from-left jagged clipPath; the previous one is still rendered
 *    underneath so you actually SEE the rip — left side = new image revealed,
 *    right side = old image still showing.
 *  - A torn-edge dark strip is painted along the wipe line during transition
 *    to make the rip tactile and visible.
 *  - Outgoing slot, in its tail phase (>PER_SLOT), nudges right + scales 1.02
 *    so it feels like paper being peeled aside.
 */

type Trans = "tear" | "punch" | "flash" | "fade";

interface Slot {
  url: string;
  trans: Trans;
  kbX?: number;
  kbY?: number;
}

const ANCHORS_BASE = "https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/anchors";
const SLOTS: Slot[] = [
  { url: `${ANCHORS_BASE}/025f0ed0-a974-45f1-8570-e378efa4c4d1.jpg`, trans: "fade", kbX:  0.3, kbY:  0.2 },
  { url: `${ANCHORS_BASE}/3dd881d8-5547-4581-9a58-255cbd51587e.jpg`, trans: "tear", kbX: -0.4, kbY:  0.1 },
  { url: `${ANCHORS_BASE}/792fe724-8f34-4375-8b0f-1deb22a3d242.jpg`, trans: "punch",kbX:  0.0, kbY:  0.5 },
  { url: `${ANCHORS_BASE}/d1a4a739-d012-4757-9a74-e630fcc0b6af.jpg`, trans: "tear", kbX:  0.5, kbY: -0.2 },
  { url: `${ANCHORS_BASE}/5b2f7171-3ecd-4e90-8682-dd07b7b8f48a.jpg`, trans: "tear", kbX: -0.3, kbY:  0.2 },
  { url: `${ANCHORS_BASE}/4e159b79-488f-4e0c-bb29-49fffe65416d.jpg`, trans: "punch",kbX:  0.0, kbY: -0.3 },
  { url: `${ANCHORS_BASE}/02555288-c7b8-4447-8f03-6fff217a07be.jpg`, trans: "tear", kbX:  0.4, kbY:  0.1 },
  { url: `${ANCHORS_BASE}/1e15d7ba-ea77-4972-85a1-53071d8d8648.jpg`, trans: "flash",kbX: -0.3, kbY:  0.2 },
  { url: `${ANCHORS_BASE}/ce747615-63c0-441b-882f-d67dd5ead677.jpg`, trans: "tear", kbX:  0.3, kbY:  0.2 },
  { url: `${ANCHORS_BASE}/06fb3732-1d9c-4250-af33-4cf5178fae96.jpg`, trans: "tear", kbX: -0.2, kbY:  0.4 },
  { url: `${ANCHORS_BASE}/af73bdc4-792c-4694-81f6-7dd1d1b7bb4d.jpg`, trans: "punch",kbX:  0.0, kbY:  0.3 },
  { url: `${ANCHORS_BASE}/f34e351e-ae30-4ca5-b465-98d6b0db188c.jpg`, trans: "tear", kbX:  0.5, kbY:  0.0 },
  { url: `${ANCHORS_BASE}/d56d5023-d025-463f-b214-dd7b1f2ca235.jpg`, trans: "tear", kbX: -0.4, kbY:  0.0 },
  { url: `${ANCHORS_BASE}/9a19ef65-6df8-403f-8ffd-ff42679536f0.jpg`, trans: "punch",kbX:  0.2, kbY:  0.4 },
  { url: `${ANCHORS_BASE}/7e7be343-15a1-4dc9-ad1d-5214dd385ef8.jpg`, trans: "tear", kbX:  0.4, kbY:  0.0 },
  { url: `${ANCHORS_BASE}/1db34754-cfa2-463c-b0f0-b641018fbdca.jpg`, trans: "tear", kbX:  0.0, kbY: -0.5 },
  { url: `${ANCHORS_BASE}/1bc0e812-cbae-4c92-a887-36c48d1ba80d.jpg`, trans: "flash",kbX:  0.2, kbY: -0.3 },
  { url: `${ANCHORS_BASE}/74c52e2f-90a1-4d1e-8a89-0c414f132ab5.jpg`, trans: "tear", kbX: -0.3, kbY:  0.2 },
  { url: `${ANCHORS_BASE}/e33b7580-5b61-41ec-878d-03e705163d5a.jpg`, trans: "punch",kbX:  0.0, kbY:  0.4 },
  { url: `${ANCHORS_BASE}/71b30d0f-cf7c-41f0-ba1a-85ee708856d4.jpg`, trans: "tear", kbX:  0.4, kbY:  0.1 },
  { url: `${ANCHORS_BASE}/09b0916f-e0ae-4104-9834-1ac4c5dc86b1.jpg`, trans: "tear", kbX: -0.4, kbY:  0.1 },
  { url: `${ANCHORS_BASE}/c1b757e1-312b-4cbe-b3a4-51f17c94934a.jpg`, trans: "tear", kbX:  0.5, kbY:  0.2 },
];

const FPS = 24;
const PER_SLOT = 16;       // base hold
const TRANSITION = 8;      // overlap window where outgoing & incoming both render — the tear
const TOTAL = SLOTS.length * PER_SLOT + 8; // 22*16 + 8 tail hold = 360 frames = 15s

// ── Stinger words ───────────────────────────────────────────────────────────
const STINGERS: { word: string; atFrame: number; hold: number }[] = [
  { word: "रहस्य",        atFrame: 32,  hold: 22 },
  { word: "तीन रानियाँ",   atFrame: 116, hold: 22 },
  { word: "बार बार टूटा",  atFrame: 212, hold: 22 },
  { word: "फिर भी खड़ा है", atFrame: 300, hold: 32 },
];

// ── Jagged tear path generator ─────────────────────────────────────────────
// Returns a polygon clipPath that covers everything LEFT of a jagged vertical
// line at xPct% across. Used by INCOMING image during transition.
function tearPathLeft(xPct: number, height: number, width: number, seed: number): string {
  const teeth = 36;
  const jagged = 22; // px amplitude — bigger = more visibly torn
  const x = (xPct / 100) * width;
  const pts: string[] = [`0px 0px`, `${x}px 0px`];
  for (let i = 1; i < teeth; i++) {
    const y = (i / teeth) * height;
    const dx = (random(`${seed}-${i}`) - 0.5) * 2 * jagged;
    pts.push(`${x + dx}px ${y}px`);
  }
  pts.push(`${x}px ${height}px`);
  pts.push(`0px ${height}px`);
  return `polygon(${pts.join(", ")})`;
}

// Coordinates of the jagged line itself (used to draw the visible torn-edge strip)
function tearLinePoints(xPct: number, height: number, width: number, seed: number): { x: number; y: number }[] {
  const teeth = 36;
  const jagged = 22;
  const x = (xPct / 100) * width;
  const pts: { x: number; y: number }[] = [{ x, y: 0 }];
  for (let i = 1; i < teeth; i++) {
    const y = (i / teeth) * height;
    const dx = (random(`${seed}-${i}`) - 0.5) * 2 * jagged;
    pts.push({ x: x + dx, y });
  }
  pts.push({ x, y: height });
  return pts;
}

// ── Visible torn-edge strip (dark fiber + highlight along the wipe boundary)
const TornEdge: React.FC<{ xPct: number; seed: number; opacity: number }> = ({
  xPct,
  seed,
  opacity,
}) => {
  const { width, height } = useVideoConfig();
  const pts = tearLinePoints(xPct, height, width, seed);
  // Build two offset polylines for a "torn paper depth" look
  const shadowPoints = pts.map((p) => `${p.x + 5},${p.y}`).join(" ");
  const linePoints = pts.map((p) => `${p.x},${p.y}`).join(" ");
  const highlightPoints = pts.map((p) => `${p.x - 3},${p.y}`).join(" ");
  return (
    <svg
      width={width}
      height={height}
      style={{ position: "absolute", inset: 0, pointerEvents: "none", opacity }}
    >
      {/* Soft drop shadow on the right side of the tear */}
      <polyline points={shadowPoints} stroke="rgba(0,0,0,0.55)" strokeWidth={10} fill="none" />
      {/* Sharp dark fiber */}
      <polyline points={linePoints} stroke="rgba(20,12,6,0.95)" strokeWidth={2.5} fill="none" />
      {/* Paper-white highlight on the left edge (newly torn) */}
      <polyline points={highlightPoints} stroke="rgba(255,240,210,0.55)" strokeWidth={1.5} fill="none" />
    </svg>
  );
};

// ── Slot renderer ───────────────────────────────────────────────────────────
const SlotImage: React.FC<{ slot: Slot; index: number }> = ({ slot, index }) => {
  const { width, height } = useVideoConfig();
  const t = useCurrentFrame(); // local: 0..PER_SLOT+TRANSITION

  // Ken Burns — slow drift across the whole slot
  const kbScale = interpolate(t, [0, PER_SLOT + TRANSITION], [1.0, 1.08], {
    extrapolateRight: "clamp",
  });
  const kbDx = interpolate(t, [0, PER_SLOT + TRANSITION], [0, (slot.kbX ?? 0) * 26]);
  const kbDy = interpolate(t, [0, PER_SLOT + TRANSITION], [0, (slot.kbY ?? 0) * 18]);

  // --- IN-phase (this slot is the incoming/top layer) ---
  let clipPath: string = "none";
  let inOpacity = 1;
  let punchScale = 1;
  let rgbSplitPx = 0;

  if (t < TRANSITION) {
    if (slot.trans === "tear" || (slot.trans === "fade" && index === 0)) {
      // Tear: jagged clipPath sweeps from 0% to 110% across TRANSITION frames
      const pct = interpolate(t, [0, TRANSITION], [-5, 110], {
        easing: Easing.inOut(Easing.cubic),
      });
      // First slot has nothing to tear from; use a soft fade instead
      if (index === 0) {
        inOpacity = interpolate(t, [0, TRANSITION], [0, 1], { extrapolateRight: "clamp" });
      } else {
        clipPath = tearPathLeft(pct, height, width, index);
      }
    } else if (slot.trans === "punch") {
      punchScale = interpolate(t, [0, 4], [1.25, 1.0], { extrapolateRight: "clamp" });
      rgbSplitPx = interpolate(t, [0, 4], [14, 0], { extrapolateRight: "clamp" });
    } else if (slot.trans === "flash") {
      inOpacity = interpolate(t, [0, 3], [0, 1], { extrapolateRight: "clamp" });
    }
  }

  // --- OUT-phase (this slot is in its tail, the next slot is being torn in
  //     on top of us — we should add a tiny peel-aside motion) ---
  let outDx = 0;
  let outScale = 1;
  if (t >= PER_SLOT) {
    const tail = t - PER_SLOT; // 0..TRANSITION
    outDx = interpolate(tail, [0, TRANSITION], [0, 16]);
    outScale = interpolate(tail, [0, TRANSITION], [1.0, 1.025]);
  }

  return (
    <AbsoluteFill style={{ overflow: "hidden", clipPath, opacity: inOpacity }}>
      {/* RGB-split layers (punch only) */}
      {rgbSplitPx > 0 && (
        <>
          <Img
            src={slot.url}
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover",
              transform: `translate(${kbDx - rgbSplitPx + outDx}px, ${kbDy}px) scale(${kbScale * punchScale * outScale})`,
              mixBlendMode: "screen", opacity: 0.6,
            }}
          />
          <Img
            src={slot.url}
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover",
              transform: `translate(${kbDx + rgbSplitPx + outDx}px, ${kbDy}px) scale(${kbScale * punchScale * outScale})`,
              filter: "hue-rotate(180deg)", mixBlendMode: "screen", opacity: 0.35,
            }}
          />
        </>
      )}
      <Img
        src={slot.url}
        style={{
          width: "100%", height: "100%", objectFit: "cover",
          transform: `translate(${kbDx + outDx}px, ${kbDy}px) scale(${kbScale * punchScale * outScale})`,
        }}
      />
      {/* Cinematic vignette */}
      <div
        style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.6) 100%)",
        }}
      />
      {/* Visible torn-edge strip during incoming tear */}
      {t < TRANSITION && slot.trans === "tear" && index > 0 && (() => {
        const pct = interpolate(t, [0, TRANSITION], [-5, 110], {
          easing: Easing.inOut(Easing.cubic),
        });
        // Edge most visible mid-wipe
        const edgeOp = interpolate(t, [0, 1, TRANSITION - 1, TRANSITION], [0, 1, 1, 0], {
          extrapolateRight: "clamp",
        });
        return <TornEdge xPct={pct} seed={index} opacity={edgeOp} />;
      })()}
    </AbsoluteFill>
  );
};

// ── Flash overlay (white) ───────────────────────────────────────────────────
const FlashOverlay: React.FC<{ atFrame: number }> = ({ atFrame }) => {
  const frame = useCurrentFrame();
  const local = frame - atFrame;
  if (local < 0 || local > 5) return null;
  const opacity = interpolate(local, [0, 1, 5], [0, 1, 0], { extrapolateRight: "clamp" });
  return <AbsoluteFill style={{ background: "white", opacity, pointerEvents: "none" }} />;
};

// ── Stinger word ────────────────────────────────────────────────────────────
const Stinger: React.FC<{ word: string; atFrame: number; hold: number }> = ({
  word, atFrame, hold,
}) => {
  const frame = useCurrentFrame();
  const local = frame - atFrame;
  if (local < 0 || local > hold) return null;
  const opacity = interpolate(local, [0, 3, hold - 4, hold], [0, 1, 1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const scale = interpolate(local, [0, 4], [1.22, 1.0], {
    extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
  });
  const skew = interpolate(local, [0, 4], [-5, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{
      display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none",
    }}>
      <div style={{
        fontFamily: "'Tiro Devanagari Hindi', 'Noto Serif Devanagari', serif",
        fontSize: 130, fontWeight: 900, color: "#fff4d6",
        letterSpacing: 2,
        textShadow: "0 4px 28px rgba(0,0,0,0.9), 0 0 70px rgba(255,180,80,0.45)",
        transform: `scale(${scale}) skewX(${skew}deg)`,
        opacity,
      }}>{word}</div>
    </AbsoluteFill>
  );
};

// ── Final fade-to-black overlay (last 24 frames) ───────────────────────────
const EndFade: React.FC = () => {
  const f = useCurrentFrame();
  const opacity = interpolate(f, [TOTAL - 24, TOTAL], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  if (opacity <= 0) return null;
  return <AbsoluteFill style={{ background: "#000", opacity, pointerEvents: "none" }} />;
};

// ── Main composition ───────────────────────────────────────────────────────
export const TeaserMontage: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: "#0a0604" }}>
      {/* Audio: 15s suspense track. The mp3 is already faded in/out, so just play it. */}
      <Audio src={staticFile("suspense_yamunotri.mp3")} />

      {/* Slot stack — each runs PER_SLOT + TRANSITION so adjacents overlap */}
      {SLOTS.map((slot, i) => (
        <Sequence
          key={i}
          from={i * PER_SLOT}
          durationInFrames={PER_SLOT + TRANSITION}
        >
          <SlotImage slot={slot} index={i} />
        </Sequence>
      ))}

      {/* Flashes on "flash" slots — overlaid at slot start */}
      {SLOTS.map((slot, i) =>
        slot.trans === "flash" ? (
          <FlashOverlay key={`f${i}`} atFrame={i * PER_SLOT} />
        ) : null,
      )}

      {/* Stingers */}
      {STINGERS.map((s, i) => (
        <Stinger key={`s${i}`} {...s} />
      ))}

      {/* Film grain */}
      <AbsoluteFill style={{
        background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.015) 0 1px, transparent 1px 3px)",
        mixBlendMode: "overlay", pointerEvents: "none",
      }} />

      <EndFade />
    </AbsoluteFill>
  );
};

export default TeaserMontage;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
